/* El pañol: en qué estado está cada envío, y qué se puede afirmar. (P-43)
 *
 * ── ES EL GEMELO DE `panol.py` DE FLOTABOT ────────────────────────────────
 *
 * Aquél decide qué se guarda cuando alguien escribe por WhatsApp; éste dibuja
 * la pantalla. Las dos mitades tienen que contestar lo MISMO sobre la misma
 * fila: si acá un envío se viera «recibido» y allá «en camino», la lista de lo
 * que falta recibir —que es el único control de todo esto— diría una cosa en
 * el teléfono y otra en el hub, y no habría forma de saber cuál mirar.
 *
 * Si tocás uno, tocá el otro. Hay una tabla de casos idéntica en
 * `panol.test.js` y en `test_panol.py`.
 *
 * ── TRES ESTADOS Y NO DOS ─────────────────────────────────────────────────
 *
 * `en_camino` · `recibido` · `usado`. El que no estaba en el pedido es el que
 * más importa: **despachado y nunca recibido** es el repuesto que se perdió en
 * el camino, y es la única lista accionable. Fundido con «recibido»
 * desaparece justo lo que se controla.
 */

/* Cuántos días sin recibir empiezan a preocupar. NO BLOQUEA NADA: decide desde
 * cuándo un envío se marca en la lista. El mismo número que `panol.py`. */
export const DIAS_EN_CAMINO = 7;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* Cuánto se usó de un envío. Los usos anulados NO cuentan: anular un uso es
 * decir que no se usó, y si siguiera contando el saldo quedaría corto para
 * siempre. */
export function usado(envio) {
  return (envio?.usos || [])
    .filter((u) => !u?.anulado_en)
    .reduce((t, u) => t + num(u?.cantidad), 0);
}

export function quedan(envio) {
  return num(envio?.cantidad) - usado(envio);
}

/* ── El estado, y el orden en que se pregunta ──────────────────────────────
 *
 * `usado` gana sobre `recibido` porque usar algo prueba que llegó; y los dos
 * ganan sobre `en_camino`. Al revés, un envío del que ya se usó la mitad
 * seguiría figurando como que no llegó — y aparecería en la lista de lo que
 * hay que ir a buscar.
 */
export function estado(envio) {
  if (!envio) return "en_camino";
  if (usado(envio) > 0) return "usado";
  if (envio.recibido_en) return "recibido";
  return "en_camino";
}

/* Si un envío en camino ya lleva demasiado. Sólo aplica a `en_camino`: uno
 * recibido hace un mes no está demorado, está entregado. */
export function demorado(envio, ahora = new Date()) {
  if (estado(envio) !== "en_camino") return false;
  const t = Date.parse(envio?.despachado_en || "");
  if (Number.isNaN(t)) return false;
  return (ahora.getTime() - t) / 86400000 >= DIAS_EN_CAMINO;
}

/* ── «Recibido» y «recibido PORQUE se usó» no son lo mismo ─────────────────
 *
 * `recibido_fuente = 'por_uso'` es una recepción DEDUCIDA: nadie confirmó que
 * llegó, se dedujo de que alguien lo usó. Dibujarla igual que una confirmada
 * afirmaría que se verificó al llegar, que es justo lo que la recepción existe
 * para probar. Es la regla de `salida_fuente` en movimientos.
 */
export function recepcionDeducida(envio) {
  return Boolean(envio?.recibido_en && envio?.recibido_fuente === "por_uso");
}

export const ETIQUETAS = {
  en_camino: "En camino",
  recibido: "Recibido",
  usado: "Usado",
};

/* El número como se lo nombra en todos lados, incluido WhatsApp. Con el número
 * pelado, «usé 2 de 45» se lee tanto como dos del envío 45 como dos del
 * interno 45 — por acá circulan internos, litros y cantidades. */
export const etiqueta = (id) => `E-${id}`;

/* Los números de arriba. Puro, y **`null` no es cero**: «no pude leer» y «no
 * hay nada» mandan a hacer cosas opuestas, y con los dos en cero la pantalla
 * diría que el pañol está al día sobre una consulta que falló. */
export function resumen(filas) {
  if (filas == null) {
    return { total: 0, enCamino: 0, recibidos: 0, usados: 0, demorados: 0,
             sinDatos: true };
  }
  const out = { total: filas.length, enCamino: 0, recibidos: 0, usados: 0,
                demorados: 0, sinDatos: false };
  for (const f of filas) {
    const e = estado(f);
    if (e === "en_camino") {
      out.enCamino += 1;
      if (demorado(f)) out.demorados += 1;
    } else if (e === "recibido") out.recibidos += 1;
    else if (e === "usado") out.usados += 1;
  }
  return out;
}

/* Los repuestos ordenados por uso, con la cuenta al lado.
 *
 * Es lo que reemplaza al catálogo mientras el vocabulario se asienta: con el
 * número a la vista la forma buena se elige sola y el dedazo se ve — «filtro
 * de aire» con 14 al lado de «filtro aire» con 1. **NADIE LOS FUSIONA**, que
 * es la regla de los clientes de `movimientos` y de `mecanicos.parecidos()`:
 * unir dos que no eran lo mismo arruina las dos cuentas sin dejar rastro.
 */
export function masUsados(filas, tope = 20) {
  const cuenta = new Map();
  for (const f of filas || []) {
    const k = f?.clave || String(f?.que || "").trim().toLowerCase();
    if (!k) continue;
    const g = cuenta.get(k) || { que: f?.que, veces: 0 };
    g.veces += 1;
    cuenta.set(k, g);
  }
  return [...cuenta.values()].sort((a, b) => b.veces - a.veces).slice(0, tope);
}
